import { IconSearch, IconCopy, IconCheck, IconUserPlus, IconUserMinus } from '@tabler/icons-react'
import { FC, useCallback, useState } from 'react'
import { SecondaryButton } from '../atoms/buttons/SecondaryButton'
import { Label } from '../atoms/label'
import { Input } from '../atoms/inputs/Input'
import { Checkbox } from '../atoms/buttons/CheckBoxButton'
import { useInView } from 'react-intersection-observer'
import Typography from '../atoms/Typography'
import { api } from 'src/api/root'

interface Props {
  selectedTenantIds: string[]
  onChange: (tenantIds: string[]) => void
  /** Tenant ids that currently have access — drives the "Has Access" badge. */
  currentTenantIds?: string[]
  /** Only fetch tenants while the host (dialog) is open. */
  enabled?: boolean
  /** Picking tenants for something other than access: no "Has Access" badges or counter. */
  hideAccessStatus?: boolean
}

const TenantAccessSelector: FC<Props> = ({
  selectedTenantIds,
  onChange,
  currentTenantIds = [],
  enabled = true,
  hideAccessStatus = false,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const { ref, inView } = useInView()

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = api.superAdmin.getTenantsByProject.useInfiniteQuery(
    { query: searchQuery, take: 40 },
    enabled,
  )

  const tenants = data?.pages.flatMap((page) => page.tenants) || []

  const copyToClipboard = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    await navigator.clipboard.writeText(id)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const toggleAllTenants = useCallback(() => {
    if (selectedTenantIds.length === tenants.length) {
      onChange([])
    } else {
      onChange(tenants.map(({ id }) => id))
    }
  }, [selectedTenantIds.length, tenants, onChange])

  const toggleTenant = (id: string) => {
    onChange(
      selectedTenantIds.includes(id) ? selectedTenantIds.filter((tid) => tid !== id) : [...selectedTenantIds, id],
    )
  }

  if (inView && hasNextPage && !isFetchingNextPage) {
    fetchNextPage()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!hideAccessStatus && (
            <>
              <Typography variant="sm" className="text-gray-400">
                Current Access: {currentTenantIds.length} tenants
              </Typography>
              <Typography variant="sm" className="text-gray-400">
                |
              </Typography>
            </>
          )}
          <Typography variant="sm" className="text-gray-400">
            Selected: {selectedTenantIds.length} tenants
          </Typography>
        </div>
        <SecondaryButton onClick={toggleAllTenants} className="h-8 px-3">
          {selectedTenantIds.length === tenants.length ? 'Deselect All' : 'Select All'}
        </SecondaryButton>
      </div>

      <div className="w-full rounded-lg bg-gray-900 p-4">
        <div className="mb-4">
          <Input
            placeholder="Search tenants by name..."
            value={searchQuery}
            icon={<IconSearch size={20} />}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-800">
          <div className="max-h-[400px] overflow-y-auto">
            {tenants.map(({ id, name }) => {
              const checked = selectedTenantIds.includes(id)
              const hasAccess = currentTenantIds.includes(id)

              return (
                <div
                  key={id}
                  onClick={() => toggleTenant(id)}
                  className={`group flex cursor-pointer items-center justify-between border-b border-gray-700 p-3 hover:bg-gray-700 ${
                    checked ? 'bg-gray-700/50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox id={id} checked={checked} />
                    <div>
                      <Label htmlFor={id} className="block text-base font-medium text-gray-50">
                        {name}
                      </Label>
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] text-gray-400">{id}</span>
                        <button
                          className="text-gray-400 opacity-0 transition-opacity group-hover:opacity-100"
                          onClick={(e) => copyToClipboard(id, e)}
                        >
                          {copiedId === id ? (
                            <IconCheck size={14} className="text-green-500" />
                          ) : (
                            <IconCopy size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  {hideAccessStatus ? null : hasAccess ? (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <IconUserPlus size={14} /> Has Access
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <IconUserMinus size={14} /> No Access
                    </span>
                  )}
                </div>
              )
            })}

            {(hasNextPage || isFetchingNextPage) && (
              <div ref={ref} className="p-3 text-center text-sm text-gray-400">
                {isFetchingNextPage ? 'Loading more...' : 'Scroll to load more'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantAccessSelector
